import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Button, Select, Modal, DatePicker, Tooltip, Badge, Tabs } from 'antd';
import { 
  SearchOutlined, 
  DownloadOutlined, 
  ReloadOutlined,
  ShoppingCartOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  BoxPlotOutlined,
  UserOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ShopOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import NavbarDropshipping from '../navbarDropshipping/navbarDropshipping';
import './misPedidos.css';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const ProductTablePedidos = () => {
  const [data, setData] = useState([]);
  const [externalData, setExternalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredExternalData, setFilteredExternalData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [externalSearchText, setExternalSearchText] = useState('');
  const [filters, setFilters] = useState({
    orderStatus: '',
    carrier: '',
    warehouse: '',
  });
  const [externalFilters, setExternalFilters] = useState({
    orderStatus: '',
    carrier: '',
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isTableScrolled, setIsTableScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [externalLoading, setExternalLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    entregados: 0,
    enDistribucion: 0,
    rechazados: 0
  });
  const [externalStats, setExternalStats] = useState({
    total: 0,
    entregados: 0,
    enDistribucion: 0,
    rechazados: 0
  });
  const [activeTab, setActiveTab] = useState('internal');

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);

  // Mapeo de los estados de la orden
  const orderStatusMap = {
    1: 'Creado',
    2: 'Distribucion',
    3: 'Entregado',
    4: 'Rechazado',
    5: 'Devolucion',
    6: 'Anulado',
    7: 'Otro',
    8: 'Error'
  };

  // Función para consumir la API de productos internos
  const user_id = localStorage.getItem('usuario_id');
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.99envios.app/api/listar_ordenes/${user_id}`);
      const result = await response.json();
      const formattedData = result
        .filter(item => item.origen_creacion === 2 || item.origen_creacion === 4) // Modified filter condition
        .map((item, index) => ({
          key: item.id?.toString() || index.toString(),
          id: item.id || index + 1,
          productName: item.nombre_producto,
          preenvioNumber: item.preeenvio.numero_preenvio,
          id_preenvio: item.preeenvio.id,
          orderDate: item.fecha_estado,
          orderDateObj: new Date(item.fecha_estado),
          client: `${item.preeenvio.destinatario.nombre} ${item.preeenvio.destinatario.primer_apellido} ${item.preeenvio.destinatario.segundo_apellido || ''}`.trim(),
          phone: item.preeenvio.destinatario.telefono,
          orderStatus: item.preeenvio.estado?.nombre || 'Desconocido',
          carrier: item.transportadora,
          shippingType: item.estado_envio,
          rawData: item,
        }))
        .sort((a, b) => {
          const dateA = new Date(a.orderDate);
          const dateB = new Date(b.orderDate);
          return dateB - dateA;
        });
      setData(formattedData);
      setFilteredData(formattedData);
      calculateStats(formattedData);
    } catch (error) {
      console.error('Error fetching internal orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para consumir la API de productos externos (dropshipping)
  const fetchExternalOrders = async () => {
    setExternalLoading(true);
    try {
      const response = await fetch(`https://api.99envios.app/api/listar_ordenes_dropshipping/${user_id}`);
      const result = await response.json();
      const formattedData = result.map((item, index) => ({
        key: item.preeenvio?.id?.toString() || index.toString(),
        id: item.preeenvio?.id || index + 1,
        productName: item.nombre_producto,
        preenvioNumber: item.preeenvio?.numero_preenvio || 'N/A',
        id_preenvio: item.preeenvio?.id,
        orderDate: item.fecha_estado,
        orderDateObj: new Date(item.fecha_estado),
        client: `${item.preeenvio?.destinatario?.nombre || ''} ${item.preeenvio?.destinatario?.primer_apellido || ''} ${item.preeenvio?.destinatario?.segundo_apellido || ''}`.trim(),
        phone: item.preeenvio?.destinatario?.telefono || 'N/A',
        orderStatus: item.preeenvio?.estado?.nombre || item.estado_envio || 'Desconocido',
        carrier: item.transportadora,
        shippingType: item.estado_envio,
        valorVenta: item.valor_venta,
        valorProveedor: item.valor_proveedor,
        valorUtilidad: item.valor_utilidad,
        rawData: item,
      }))
      .sort((a, b) => {
        const dateA = new Date(a.orderDate);
        const dateB = new Date(b.orderDate);
        return dateB - dateA;
      });
      setExternalData(formattedData);
      setFilteredExternalData(formattedData);
      calculateExternalStats(formattedData);
    } catch (error) {
      console.error('Error fetching external orders:', error);
    } finally {
      setExternalLoading(false);
    }
  };

  // Función para calcular estadísticas de productos internos
  const calculateStats = (orders) => {
    const stats = {
      total: orders.length,
      entregados: orders.filter(order => order.orderStatus === 'Entregado').length,
      enDistribucion: orders.filter(order => order.orderStatus === 'Distribucion').length,
      rechazados: orders.filter(order => order.orderStatus === 'Rechazado').length
    };
    setStats(stats);
  };

  // Función para calcular estadísticas de productos externos
  const calculateExternalStats = (orders) => {
    const stats = {
      total: orders.length,
      entregados: orders.filter(order => order.orderStatus === 'entregado').length,
      enDistribucion: orders.filter(order => order.orderStatus === 'distribucion' || order.orderStatus === 'creado').length,
      rechazados: orders.filter(order => order.orderStatus === 'rechazado').length
    };
    setExternalStats(stats);
  };

  // Carga los datos al montar el componente
  useEffect(() => {
    fetchOrders();
    fetchExternalOrders();
  }, []);

  // Sincronizar el desplazamiento de las barras
  const syncScroll = (sourceRef, targetRef) => {
    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleExternalSearch = (value) => {
    setExternalSearchText(value);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
  };

  const handleExternalFilterChange = (filterName, value) => {
    setExternalFilters({ ...externalFilters, [filterName]: value });
  };

  useEffect(() => {
    let result = data;
    if (searchText) {
      result = result.filter(
        (item) => 
          item.id.toString().includes(searchText) ||
          item.productName.toLowerCase().includes(searchText.toLowerCase()) ||
          item.preenvioNumber.toLowerCase().includes(searchText.toLowerCase()) ||
          item.client.toLowerCase().includes(searchText.toLowerCase()) ||
          item.phone.includes(searchText)
      );
    }
    if (filters.orderStatus) {
      result = result.filter((item) => item.orderStatus === filters.orderStatus);
    }
    if (filters.carrier) {
      result = result.filter((item) => item.carrier === filters.carrier);
    }
    if (filters.warehouse) {
      result = result.filter((item) => item.warehouse === filters.warehouse);
    }
    
    // Mantener el ordenamiento por fecha después de filtrar
    result = result.sort((a, b) => {
      const dateA = new Date(a.orderDate);
      const dateB = new Date(b.orderDate);
      return dateB - dateA; // Descendente: más reciente primero
    });
    
    setFilteredData(result);
    console.log('Datos filtrados para tabla:', result.slice(0, 5).map(item => ({ 
      id: item.id, 
      date: item.orderDate,
      formatted: new Date(item.orderDate).toLocaleDateString('es-CO') 
    })));
  }, [searchText, filters, data]);

  // Filtrado para productos externos
  useEffect(() => {
    let result = externalData;
    if (externalSearchText) {
      result = result.filter(
        (item) => 
          item.id.toString().includes(externalSearchText) ||
          item.productName.toLowerCase().includes(externalSearchText.toLowerCase()) ||
          item.preenvioNumber.toLowerCase().includes(externalSearchText.toLowerCase()) ||
          item.client.toLowerCase().includes(externalSearchText.toLowerCase()) ||
          item.phone.includes(externalSearchText)
      );
    }
    if (externalFilters.orderStatus) {
      result = result.filter((item) => item.orderStatus === externalFilters.orderStatus);
    }
    if (externalFilters.carrier) {
      result = result.filter((item) => item.carrier === externalFilters.carrier);
    }
    
    // Mantener el ordenamiento por fecha después de filtrar
    result = result.sort((a, b) => {
      const dateA = new Date(a.orderDate);
      const dateB = new Date(b.orderDate);
      return dateB - dateA;
    });
    
    setFilteredExternalData(result);
  }, [externalSearchText, externalFilters, externalData]);



  const showModal = (record) => {
    setSelectedOrder(record.rawData);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Creado': 'status-creado',
      'Distribucion': 'status-distribucion',
      'Entregado': 'status-entregado',
      'Rechazado': 'status-rechazado',
      'Devolucion': 'status-devolucion',
      'Anulado': 'status-anulado',
      'Otro': 'status-otro',
      'Error': 'status-error'
    };
    return `status-badge ${statusMap[status] || 'status-otro'}`;
  };

  const columns = [
    {
      title: '',
      dataIndex: 'checkbox',
      key: 'checkbox',
      render: () => <input type="checkbox" />,
    },
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Nombre del producto',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Número de preenvío',
      dataIndex: 'preenvioNumber',
      key: 'preenvioNumber',
    },
    {
      title: 'ID preenvío',
      dataIndex: 'id_preenvio',
      key: 'id_preenvio',
    },
    {
      title: 'Fecha de la orden',
      dataIndex: 'orderDate',
      key: 'orderDate',
      sorter: (a, b) => new Date(b.orderDate) - new Date(a.orderDate),
      render: (date) => {
        const formattedDate = new Date(date).toLocaleDateString('es-CO', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        return <span style={{ color: '#666' }}>{formattedDate}</span>;
      },
    },
    {
      title: 'Cliente',
      dataIndex: 'client',
      key: 'client',
    },
    {
      title: 'Teléfono',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Estatus de la Orden',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      render: (status) => (
        <span className={getStatusBadgeClass(status)}>
          {status}
        </span>
      ),
    },
    {
      title: 'Transportadora',
      dataIndex: 'carrier',
      key: 'carrier',
    },
    {
      title: 'Tipo de Envío',
      dataIndex: 'shippingType',
      key: 'shippingType',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <div className="row-actions">
          <Tooltip title="Ver detalles">
            <button className="action-button action-view" onClick={() => showModal(record)}>
              <EyeOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Editar">
            <button className="action-button action-edit">
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Eliminar">
            <button className="action-button action-delete">
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    }
  ];

  // Columnas para la tabla de productos externos (dropshipping)
  const externalColumns = [
    {
      title: '',
      dataIndex: 'checkbox',
      key: 'checkbox',
      render: () => <input type="checkbox" />,
    },
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Nombre del producto',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Número de preenvío',
      dataIndex: 'preenvioNumber',
      key: 'preenvioNumber',
    },
    {
      title: 'Valor Venta',
      dataIndex: 'valorVenta',
      key: 'valorVenta',
      render: (value) => (
        <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
          ${value?.toLocaleString('es-CO') || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Valor Proveedor',
      dataIndex: 'valorProveedor',
      key: 'valorProveedor',
      render: (value) => (
        <span style={{ color: '#f57c00' }}>
          ${value?.toLocaleString('es-CO') || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Utilidad',
      dataIndex: 'valorUtilidad',
      key: 'valorUtilidad',
      render: (value) => (
        <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
          ${value?.toLocaleString('es-CO') || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Fecha de la orden',
      dataIndex: 'orderDate',
      key: 'orderDate',
      sorter: (a, b) => new Date(b.orderDate) - new Date(a.orderDate),
      render: (date) => {
        const formattedDate = new Date(date).toLocaleDateString('es-CO', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        return <span style={{ color: '#666' }}>{formattedDate}</span>;
      },
    },
    {
      title: 'Cliente',
      dataIndex: 'client',
      key: 'client',
    },
    {
      title: 'Teléfono',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Estatus de la Orden',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      render: (status) => (
        <span className={getStatusBadgeClass(status)}>
          {status}
        </span>
      ),
    },
    {
      title: 'Transportadora',
      dataIndex: 'carrier',
      key: 'carrier',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <div className="row-actions">
          <Tooltip title="Ver detalles">
            <button className="action-button action-view" onClick={() => showModal(record)}>
              <EyeOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Editar">
            <button className="action-button action-edit">
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Eliminar">
            <button className="action-button action-delete">
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    }
  ];

  const exportToExcel = () => {
    // Prepare the data for export
    const exportData = filteredData.map(item => ({
      'ID': item.id,
      'Nombre del producto': item.productName,
      'Fecha de la orden': item.orderDate,
      'Cliente': item.client,
      'Teléfono': item.phone,
      'Estatus de la Orden': item.orderStatus,
      'Transportadora': item.carrier,
      'Tipo de Envío': item.shippingType
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');

    // Save file
    XLSX.writeFile(wb, 'pedidos.xlsx');
  };

  const handleTableScroll = (e) => {
    setIsTableScrolled(e.target.scrollTop > 0);
    syncScroll(tableContainerRef, topScrollRef);
  };

  const getResponsiveColumns = () => {
    const isMobile = window.innerWidth <= 480;
    return columns.filter(col => {
      if (isMobile) {
        return !['checkbox', 'id'].includes(col.key);
      }
      return true;
    });
  };

  // Función para actualizar datos
  const handleRefresh = () => {
    if (activeTab === 'internal') {
      fetchOrders();
    } else {
      fetchExternalOrders();
    }
  };

  return (
    <>
      <NavbarDropshipping title="Envíos Dropshipping"/>
      <div className='misPedidos'>
        {/* Header con acciones */}
        <div className="page-header">
          <h1 className="page-title">Mis Pedidos</h1>
          <div className="page-actions">
            <button className="refresh-button" onClick={handleRefresh}>
              <ReloadOutlined />
            </button>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              className="export-button"
            >
              Exportar Excel
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="filters">
          <div className="search-group">
            <Input
              placeholder="Buscar por ID, producto, preenvío, cliente..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
              size="large"
            />
          </div>
          <div className="filter-group">
            <Select
              placeholder="Estado del pedido"
              onChange={(value) => handleFilterChange('orderStatus', value)}
              allowClear
              size="large"
              className="status-select"
            >
              <Option value="">Todos los estados</Option>
              <Option value="Creado">
                <Badge color="#1976d2" text="Creado" />
              </Option>
              <Option value="Distribucion">
                <Badge color="#f57c00" text="En Distribución" />
              </Option>
              <Option value="Entregado">
                <Badge color="#2e7d32" text="Entregado" />
              </Option>
              <Option value="Rechazado">
                <Badge color="#c62828" text="Rechazado" />
              </Option>
              <Option value="Devolucion">
                <Badge color="#c2185b" text="Devolución" />
              </Option>
              <Option value="Anulado">
                <Badge color="#7b1fa2" text="Anulado" />
              </Option>
              <Option value="Error">
                <Badge color="#e65100" text="Error" />
              </Option>
            </Select>
            <Select
              placeholder="Transportadora"
              onChange={(value) => handleFilterChange('carrier', value)}
              allowClear
              size="large"
              className="carrier-select"
            >
              <Option value="">Todas las transportadoras</Option>
              <Option value="interrapidisimo">
                <CarOutlined /> Interrapidisimo
              </Option>
              <Option value="coordinadora">
                <CarOutlined /> Coordinadora
              </Option>
              <Option value="servientrega">
                <CarOutlined /> Servientrega
              </Option>
              <Option value="envia">
                <CarOutlined /> Envía
              </Option>
            </Select>
          </div>
        </div>

        {/* Pestañas para productos internos y externos */}
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          className="products-tabs"
          size="large"
        >
          <TabPane 
            tab={
              <span>
                <ShopOutlined />
                Productos Internos ({stats.total})
              </span>
            } 
            key="internal"
          >
            {/* Dashboard de estadísticas para productos internos */}
            <div className="stats-dashboard">
              <div className="stat-card stat-info">
                <div className="stat-icon">
                  <ShoppingCartOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.total}</h3>
                  <p>Total de Pedidos</p>
                </div>
              </div>
              <div className="stat-card stat-success">
                <div className="stat-icon">
                  <CheckCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.entregados}</h3>
                  <p>Entregados</p>
                </div>
              </div>
              <div className="stat-card stat-warning">
                <div className="stat-icon">
                  <CarOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.enDistribucion}</h3>
                  <p>En Distribución</p>
                </div>
              </div>
              <div className="stat-card stat-danger">
                <div className="stat-icon">
                  <CloseCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{stats.rechazados}</h3>
                  <p>Rechazados</p>
                </div>
              </div>
            </div>

            {/* Filtros para productos internos */}
            <div className="filters">
              <div className="search-group">
                <Input
                  placeholder="Buscar por ID, producto, preenvío, cliente..."
                  prefix={<SearchOutlined />}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="search-input"
                  size="large"
                />
              </div>
              <div className="filter-group">
                <Select
                  placeholder="Estado del pedido"
                  onChange={(value) => handleFilterChange('orderStatus', value)}
                  allowClear
                  size="large"
                  className="status-select"
                >
                  <Option value="">Todos los estados</Option>
                  <Option value="Creado">
                    <Badge color="#1976d2" text="Creado" />
                  </Option>
                  <Option value="Distribucion">
                    <Badge color="#f57c00" text="En Distribución" />
                  </Option>
                  <Option value="Entregado">
                    <Badge color="#2e7d32" text="Entregado" />
                  </Option>
                  <Option value="Rechazado">
                    <Badge color="#c62828" text="Rechazado" />
                  </Option>
                </Select>
                <Select
                  placeholder="Empresa transportadora"
                  onChange={(value) => handleFilterChange('carrier', value)}
                  allowClear
                  size="large"
                  className="carrier-select"
                >
                  <Option value="">Todas las transportadoras</Option>
                  <Option value="interrapidisimo">
                    <CarOutlined /> Interrapidisimo
                  </Option>
                  <Option value="coordinadora">
                    <CarOutlined /> Coordinadora
                  </Option>
                  <Option value="servientrega">
                    <CarOutlined /> Servientrega
                  </Option>
                  <Option value="envia">
                    <CarOutlined /> Envía
                  </Option>
                </Select>
              </div>
            </div>

            {/* Tabla de productos internos */}
            <div className="misPedidos-tabla">
              {loading && (
                <div className="loading-overlay">
                  <div className="loading-spinner">
                    <ReloadOutlined spin />
                  </div>
                </div>
              )}
              <Table
                columns={getResponsiveColumns()}
                dataSource={filteredData}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} elementos`,
                }}
                scroll={{ x: 1200 }}
                size="small"
                onScroll={handleTableScroll}
                className={isTableScrolled ? 'scrolled' : ''}
              />
            </div>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <GlobalOutlined />
                Productos Externos ({externalStats.total})
              </span>
            } 
            key="external"
          >
            {/* Dashboard de estadísticas para productos externos */}
            <div className="stats-dashboard">
              <div className="stat-card stat-info">
                <div className="stat-icon">
                  <ShoppingCartOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.total}</h3>
                  <p>Total de Pedidos</p>
                </div>
              </div>
              <div className="stat-card stat-success">
                <div className="stat-icon">
                  <CheckCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.entregados}</h3>
                  <p>Entregados</p>
                </div>
              </div>
              <div className="stat-card stat-warning">
                <div className="stat-icon">
                  <CarOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.enDistribucion}</h3>
                  <p>En Distribución</p>
                </div>
              </div>
              <div className="stat-card stat-danger">
                <div className="stat-icon">
                  <CloseCircleOutlined />
                </div>
                <div className="stat-info">
                  <h3>{externalStats.rechazados}</h3>
                  <p>Rechazados</p>
                </div>
              </div>
            </div>

            {/* Filtros para productos externos */}
            <div className="filters">
              <div className="search-group">
                <Input
                  placeholder="Buscar por ID, producto, preenvío, cliente..."
                  prefix={<SearchOutlined />}
                  onChange={(e) => handleExternalSearch(e.target.value)}
                  className="search-input"
                  size="large"
                />
              </div>
              <div className="filter-group">
                <Select
                  placeholder="Estado del pedido"
                  onChange={(value) => handleExternalFilterChange('orderStatus', value)}
                  allowClear
                  size="large"
                  className="status-select"
                >
                  <Option value="">Todos los estados</Option>
                  <Option value="creado">
                    <Badge color="#1976d2" text="Creado" />
                  </Option>
                  <Option value="distribucion">
                    <Badge color="#f57c00" text="En Distribución" />
                  </Option>
                  <Option value="entregado">
                    <Badge color="#2e7d32" text="Entregado" />
                  </Option>
                  <Option value="rechazado">
                    <Badge color="#c62828" text="Rechazado" />
                  </Option>
                </Select>
                <Select
                  placeholder="Empresa transportadora"
                  onChange={(value) => handleExternalFilterChange('carrier', value)}
                  allowClear
                  size="large"
                  className="carrier-select"
                >
                  <Option value="">Todas las transportadoras</Option>
                  <Option value="interrapidisimo">
                    <CarOutlined /> Interrapidisimo
                  </Option>
                  <Option value="coordinadora">
                    <CarOutlined /> Coordinadora
                  </Option>
                  <Option value="servientrega">
                    <CarOutlined /> Servientrega
                  </Option>
                  <Option value="envia">
                    <CarOutlined /> Envía
                  </Option>
                </Select>
              </div>
            </div>

            {/* Tabla de productos externos */}
            <div className="misPedidos-tabla">
              {externalLoading && (
                <div className="loading-overlay">
                  <div className="loading-spinner">
                    <ReloadOutlined spin />
                  </div>
                </div>
              )}
              <Table
                columns={externalColumns}
                dataSource={filteredExternalData}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} elementos`,
                }}
                scroll={{ x: 1400 }}
                size="small"
                onScroll={handleTableScroll}
                className={isTableScrolled ? 'scrolled' : ''}
              />
            </div>
          </TabPane>
        </Tabs>
      </div>

      {/* Modal de detalles mejorado */}
      <Modal
        title="Detalles del Envío"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        className="pedido-detail-modal"
        width={800}
        footer={[
          <Button key="submit" type="primary" onClick={handleOk}>
            Cerrar
          </Button>
        ]}
      >
        {selectedOrder && (
          <div className="detail-info-grid">
            <div className="detail-section">
              <h3><BoxPlotOutlined /> Información del Producto</h3>
              <div className="detail-info-item">
                <label>Nombre del producto</label>
                <span>{selectedOrder?.nombre_producto || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Valor de venta</label>
                <span>${selectedOrder?.valor_venta?.toLocaleString() || '0'}</span>
              </div>
              <div className="detail-info-item">
                <label>Valor envío</label>
                <span>${selectedOrder?.valor_envio || '0'}</span>
              </div>
            </div>
            
            <div className="detail-section">
              <h3><CarOutlined /> Información del Envío</h3>
              <div className="detail-info-item">
                <label>Transportadora</label>
                <span>{selectedOrder?.transportadora || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Estado envío</label>
                <span className={getStatusBadgeClass(selectedOrder?.estado_envio)}>
                  {selectedOrder?.estado_envio || 'N/A'}
                </span>
              </div>
            </div>
            
            <div className="detail-section">
              <h3><UserOutlined /> Información del Destinatario</h3>
              <div className="detail-info-item">
                <label>Nombre completo</label>
                <span>{`${selectedOrder?.preeenvio?.destinatario?.nombre || ''} ${selectedOrder?.preeenvio?.destinatario?.primer_apellido || ''} ${selectedOrder?.preeenvio?.destinatario?.segundo_apellido || ''}`.trim() || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Teléfono</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.telefono || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Correo</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.correo || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Dirección</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.direccion || 'N/A'}</span>
              </div>
              <div className="detail-info-item">
                <label>Ciudad</label>
                <span>{selectedOrder?.preeenvio?.destinatario?.ciudad || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ProductTablePedidos;
